/**
 * DebugLog — Circular buffer logging system for Spellroads.
 * Must be initialized before any other module.
 *
 * @module DebugLog
 */

const MAX_ENTRIES = 1000;

const DebugLog = {
  /** @type {Array<{level: string, message: string, timestamp: number, timeStr: string}>} */
  _entries: [],

  _enabled: true,
  _verboseMode: false,

  /**
   * Initialize DebugLog. Sets up global error handlers.
   * Call this as the very first thing in main.js.
   */
  init() {
    window.onerror = (msg, src, line, col, err) => {
      this.error(`[boot] Uncaught: ${msg} @ ${src}:${line}:${col}`);
    };

    window.onunhandledrejection = (e) => {
      const reason = e.reason instanceof Error
        ? `${e.reason.message} (${e.reason.stack?.split('\n')[1]?.trim() ?? 'no stack'})`
        : String(e.reason);
      this.error(`[boot] UnhandledRejection: ${reason}`);
    };

    this.info('[boot] DebugLog initialized');
  },

  /**
   * Core logging method. Stores entry in circular buffer.
   * @param {'info'|'warn'|'error'|'event'} level
   * @param {string} message
   */
  _log(level, message) {
    const entry = {
      level,
      message,
      timestamp: Date.now(),
      timeStr: new Date().toISOString(),
    };

    this._entries.push(entry);
    if (this._entries.length > MAX_ENTRIES) {
      this._entries.shift();
    }

    if (this._verboseMode || level === 'error') {
      const consoleFn = level === 'event' ? 'log' : level;
      // eslint-disable-next-line no-console
      console[consoleFn](`[${entry.timeStr}] ${message}`);
    }
  },

  /**
   * Log an informational message.
   * @param {string} msg
   */
  info(msg) { this._log('info', msg); },

  /**
   * Log a warning.
   * @param {string} msg
   */
  warn(msg) { this._log('warn', msg); },

  /**
   * Log an error. Always printed to console regardless of verbose mode.
   * @param {string} msg
   */
  error(msg) { this._log('error', msg); },

  /**
   * Log a game event (combat, quests, encounters, etc.).
   * Format: '[category] description; key=value pairs'
   * @param {string} msg
   */
  event(msg) { this._log('event', msg); },

  /**
   * Enable or disable verbose mode (print all logs to console).
   * @param {boolean} enabled
   */
  setVerbose(enabled) {
    this._verboseMode = enabled;
    this.info(`[debug] Verbose mode ${enabled ? 'enabled' : 'disabled'}`);
  },

  /**
   * Return a copy of all log entries.
   * @returns {Array<object>}
   */
  getAll() { return [...this._entries]; },

  /**
   * Return only error-level entries.
   * @returns {Array<object>}
   */
  getErrors() { return this._entries.filter(e => e.level === 'error'); },

  /**
   * Return the last N entries.
   * @param {number} n
   * @returns {Array<object>}
   */
  getLast(n) { return this._entries.slice(-n); },

  /**
   * Clear all log entries.
   */
  clear() { this._entries = []; },

  /**
   * Upload logs to the debug server (only if user has consented).
   * Consent stored in localStorage key 'spellroads_log_consent'.
   * @returns {Promise<boolean>} true if uploaded successfully
   */
  async upload() {
    if (localStorage.getItem('spellroads_log_consent') !== 'true') {
      // eslint-disable-next-line no-console
      console.warn('[DebugLog] Log upload declined by user (no consent)');
      return false;
    }

    const payload = {
      version: window.__gameVersion ?? 'unknown',
      timestamp: Date.now(),
      party: window.__debug?.getPartyState?.() ?? null,
      region: window.__debug?.getRegion?.() ?? null,
      logs: this.getAll(),
    };

    try {
      const response = await fetch('http://localhost:3001/api/logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        this.error(`[debug] Log upload HTTP error: ${response.status}`);
        return false;
      }

      this.info('[debug] Logs uploaded successfully');
      return true;
    } catch (e) {
      this.error(`[debug] Log upload failed: ${e.message}`);
      return false;
    }
  },
};

export default DebugLog;
